
import axios from 'axios';
import _ from 'lodash';


import { getSignInUrl } from '../configuration/selectors';


/*
* NOTE: This is a temporary import to mitigate issues where the loaded user/credentials are not
* compatible with the running application.
 */
import * as authSelectors from './selectors';


// FIXME: Consistent action naming conventions
export const types = {
  SET_USER: 'authentication/SET_USER',
  CLEAR_USER: 'authentication/CLEAR_USER',

  SET_CREDENTIALS: 'authentication/SET_CREDENTIALS',
  CLEAR_CREDENTIALS: 'authentication/CLEAR_CREDENTIALS',

  LOGIN_REQUEST: 'authentication/LOGIN_REQUEST',
  LOGIN_SUCCESS: 'authentication/LOGIN_SUCCESS',
  LOGIN_FAILURE: 'authentication/LOGIN_FAILURE',
  LOGIN_CLEAR: 'authentication/LOGIN_CLEAR',
};

/*
* Login flow actions
 */
export const loginRequest = () => { return { type: types.LOGIN_REQUEST }; };
export const loginSuccess = () => { return { type: types.LOGIN_SUCCESS }; };
export const loginFailure = (errorMessage) => {
  return {
    type: types.LOGIN_FAILURE,
    errorMessage,
  };
};
export const loginClear = () => { return { type: types.LOGIN_CLEAR }; };


/*
* User actions
 */
export const setUser = (user) => {
  return {
    type: types.SET_USER,
    user,
  };
};


export const clearUser = () => {
  return {
    type: types.CLEAR_USER,
  };
};


export const saveUser = (user) => {
  return (dispatch, getState) => {
    const userItem = JSON.stringify(user);
    localStorage.setItem('user', userItem);
    dispatch(setUser(user));
  };
};


export const deleteUser = () => {
  return (dispatch, getState) => {
    localStorage.removeItem('user');
    dispatch(clearUser());
  };
};


export const loadUser = () => {
  return (dispatch, getState) => {
    const userItem = localStorage.getItem('user');
    if (!userItem) {
      return false;
    }

    const user = JSON.parse(userItem);
    dispatch(setUser(user));
    return true;
  };
};


/*
* Credentials actions
 */
export const setCredentials = (credentials) => {
  return {
    type: types.SET_CREDENTIALS,
    credentials,
  };
};


export const clearCredentials = () => {
  return {
    type: types.CLEAR_CREDENTIALS,
  };
};


export const saveCredentials = (credentials) => {
  return (dispatch, getState) => {
    const credentialsItem = JSON.stringify(credentials);
    localStorage.setItem('credentials', credentialsItem);
    dispatch(setCredentials(credentials));
  };
};


export const deleteCredentials = () => {
  return (dispatch, getState) => {
    localStorage.removeItem('credentials');
    localStorage.removeItem('responseHeaders');
    dispatch(clearCredentials());
  };
};


export const loadCredentials = () => {
  return (dispatch, getState) => {
    const credentialsItem = localStorage.getItem('credentials');
    if (!credentialsItem) {
      return false;
    }

    const credentials = JSON.parse(credentialsItem);
    dispatch(setCredentials(credentials));
    return true;
  };
};


/*
* Bootstrapping actions
 */
export const loadAuthentication = () => {
  return (dispatch, getState) => {
    const gotCred = dispatch(loadCredentials());
    const gotUser = dispatch(loadUser());

    if (!(gotCred && gotUser)) {
      return;
    }

    // Verify configuration by asserting that all selectors return defined outputs
    try {
      const state = getState();
      const selectorValues = _.values(authSelectors).map((f) => {
        return f(state);
      });
      const configurationUndefined = selectorValues.some((value) => {
        return value === undefined;
      });
      if (configurationUndefined) {
        // NOTE: Catch is local to centralize error handling for any failure of the configuration
        // verification process.
        throw new Error('');
      }
    } catch (e) {
      // NOTE: Can not dispatch logoutUser() as it expects the apollo client to be set.
      /* eslint-disable no-console */
      console.warn('Cached configuration is invalid. Clearing all cached values.');
      /* eslint-enable no-console */

      dispatch(loginClear());
      dispatch(deleteCredentials());
      dispatch(deleteUser());
      return;
    }

    dispatch(loginSuccess());
  };
};


/*
* Login/Logout actions
 */
export const loginUser = (email, password) => {
  /*
  * There is a lot going on here. Much of it needs to be factored out. Here is the synopsis:
  *
  * Happy Path:
  *   1) Get sign in URL from state
  *   2) Signal login request start
  *   3) Marshal data off response headers and body
  *   4) Save data
  *   5) Signal success
  * Sad Path:
  *   1) Check if error is an axios error
  *   2) Check if status is 401
  *   3) Check if data has errors
  *   4) Signal failure with message based on error
   */
  return async (dispatch, getState, { client }) => {
    const signInUrl = getSignInUrl(getState());
    const credentialsProperties = ['access-token', 'uid', 'expiry', 'client', 'token-type'];
    try {
      dispatch(loginRequest());
      const response = await axios.post(signInUrl, { email, password });
      const { headers, data: { data: user } } = response;
      const credentials = _.pick(headers, credentialsProperties);
      dispatch(saveCredentials(credentials));
      dispatch(saveUser(user));
      dispatch(loginSuccess());
    } catch (e) {
      if (!(e.response && e.response.status && e.response.data && e.response.data.errors)) {
        dispatch(loginFailure(`Unknown error: ${e.message}`));
        throw e;
      } else {
        const { errors } = e.response.data;
        const errorMessage = errors.join(' ');
        dispatch(loginFailure(errorMessage));
      }
    }
  };
};


// TODO: Accept message?
export const logoutUser = () => {
  return async (dispatch, getState, { client }) => {
    dispatch(loginClear());
    dispatch(deleteCredentials());
    dispatch(deleteUser());
    await client.resetStore();
  };
};
